import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatDisplayMonth } from "../../../Helper/functionHelper.js";
import useLoader from "../../../hooks/useLoader.js";
import { getMonthlyParityDetail } from "../../../services/monthlyParity.js";

const money = (v) => (v === null || v === undefined ? "N/A" : `₹${Number(v).toFixed(2)}`);

// Same section keys the backend stores on pms_users._Payroll /
// pms_salary_slip_shadow.payroll_detail - see PAYROLL_SECTION_KEYS in
// Kasturi-Node-Backend/Config/constants.js.
const SECTION_ORDER = ["Standard Monthly Salary", "Extra Earnings", "Employer Deduction", "Employee Deduction"];

const DiffRow = ({ label, diff }) => {
  if (!diff) {
    return (
      <div className="col-6 col-md-3 mb-2">
        <div className="text-muted" style={{ fontSize: "11px" }}>
          {label}
        </div>
        <div className="fw-semibold" style={{ fontSize: "13px" }}>
          <span className="text-muted">No CI4 slip</span>
        </div>
      </div>
    );
  }
  return (
    <div className="col-6 col-md-3 mb-2">
      <div className="text-muted" style={{ fontSize: "11px" }}>
        {label}
      </div>
      <div style={{ fontSize: "13px" }}>
        <div>Node: {money(diff.node)}</div>
        <div>CI4: {money(diff.ci4)}</div>
        <div className={`fw-semibold ${diff.matches ? "text-success" : "text-danger"}`}>
          {diff.matches ? "Matches" : `Δ ${diff.delta}`}
        </div>
      </div>
    </div>
  );
};

const SectionTable = ({ title, heads }) => {
  if (!heads || heads.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
        {title}
      </div>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Head</th>
            <th className="text-end">Monthly Amount</th>
            <th className="text-end">Pro-rated Amount</th>
          </tr>
        </thead>
        <tbody>
          {heads.map((h, i) => (
            <tr key={i}>
              <td>{h.categoryName || h.id}</td>
              <td className="text-end">{money(h.monthlyAmount)}</td>
              <td className="text-end">{money(h.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyParityDetail = ({ recordId }) => {
  const { showLoader, hideLoader } = useLoader();
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      showLoader();
      try {
        const response = await getMonthlyParityDetail(recordId);
        setDetail(response?.data?.data);
      } catch (error) {
        console.error("Error fetching parity detail:", error);
        toast.error(error?.response?.data?.message || "Failed to load parity record");
      } finally {
        hideLoader();
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  if (!detail) return null;

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row mb-2">
        <div className="col-6 col-md-4">
          <div className="text-muted" style={{ fontSize: "11px" }}>
            Employee
          </div>
          <div className="fw-semibold" style={{ fontSize: "13px" }}>
            {detail.employee_name} ({detail.employee_code})
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="text-muted" style={{ fontSize: "11px" }}>
            Month
          </div>
          <div className="fw-semibold" style={{ fontSize: "13px" }}>
            {formatDisplayMonth(detail.period?.month_year)}
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="text-muted" style={{ fontSize: "11px" }}>
            Days Worked
          </div>
          <div className="fw-semibold" style={{ fontSize: "13px" }}>
            {detail.days_worked} / {detail.total_days} (LWP: {detail.lwp_days})
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <DiffRow label="Gross" diff={detail.diff?.gross} />
        <DiffRow label="Deduction" diff={detail.diff?.deduction} />
        <DiffRow label="Net Pay" diff={detail.diff?.net_pay} />
        <DiffRow label="Days Worked" diff={detail.diff?.days_worked} />
      </div>

      {SECTION_ORDER.map((key) => (
        <SectionTable key={key} title={key} heads={detail.node?.sections?.[key]} />
      ))}

      {detail.not_yet_compared?.length > 0 && (
        <div className="mt-2">
          <div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
            Not yet compared (excluded from this diff):
          </div>
          <ul className="mb-0" style={{ fontSize: "12px" }}>
            {detail.not_yet_compared.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MonthlyParityDetail;
